import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "appSource" }
const _hoisted_2 = { class: "button-row source" }
const _hoisted_3 = ["options", "value"]
const _hoisted_4 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_5 = { class: "button-row" }
const _hoisted_6 = ["value", "label"]
const _hoisted_7 = {
  key: 1,
  class: "spacer source"
}
const _hoisted_8 = { class: "button-row" }
const _hoisted_9 = ["value", "label"]
const _hoisted_10 = {
  key: 2,
  class: "spacer source"
}
const _hoisted_11 = ["value", "label"]
const _hoisted_12 = { class: "spacer source" }
const _hoisted_13 = { class: "spacer source" }
const _hoisted_14 = ["value", "label"]
const _hoisted_15 = {
  key: 0,
  class: "error"
}
const _hoisted_16 = { class: "spacer source" }
const _hoisted_17 = ["value", "label", "disabled"]
const _hoisted_18 = { class: "spacer source" }
const _hoisted_19 = {
  key: 0,
  class: "spacer"
}
const _hoisted_20 = { key: 1 }
const _hoisted_21 = {
  key: 0,
  class: "spacer source builder-image"
}
const _hoisted_22 = ["value", "disabled"]

import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import jsyaml from 'js-yaml';

import Application from '../../models/applications';
import GitPicker from './GitPicker.vue';
import { sortBy } from '@shell/utils/sort';
import { generateZip } from '@shell/utils/download';
import {
  APPLICATION_SOURCE_TYPE,
  EpinioApplicationChartResource,
  EpinioInfo,
  EpinioAppSource,
  EPINIO_APP_MANIFEST
} from '../../types';
import { EpinioAppInfo } from '../../types';
import { _EDIT } from '@shell/config/query-params';
import { AppUtils } from '../../utils/application';
import { EPINIO_TYPES } from '../../types';
import ResourceDropdown from './ResourceDropdown.vue';

interface FileWithRelativePath extends File {
  // For some reason TS throws this as missing at transpile time .. so recreate it
   readonly webkitRelativePath: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppSource',
  props: {
    application: {},
    source: {},
    info: {},
    mode: {}
  },
  emits: ["change", "changeAppInfo", "changeAppConfig", "valid"],
  setup(__props: any, { emit: __emit }) {


const GIT_BASE_URL = {
  [APPLICATION_SOURCE_TYPE.GIT_HUB]: 'https://github.com',
  [APPLICATION_SOURCE_TYPE.GIT_LAB]: 'https://gitlab.com',
};

const store = useStore();

const t = store.getters['i18n/t'];

const props = __props;

const emit = __emit;

const isEdit = computed(() => props.mode === _EDIT);
const isView = computed(() => props.mode === 'view');

const manifestFileInput = ref<HTMLInputElement | null>(null);
const archiveFileInput = ref<HTMLInputElement | null>(null);
const folderFileInput = ref<HTMLInputElement | null>(null);
const fileDialogActive = ref(false);

const isLoadingGitConfigs = ref(false);
const gitConfigs = ref<any[]>([]);
const cachedGitConfigs = ref<any[]>([]);

const isLoadingAppCharts = ref(false);
const appCharts = ref<any[]>([]);
const cachedAppCharts = ref<any[]>([]);

const isLoadingBuilderImages = ref(false);
const builderImages = ref<any[]>([]);
const cachedBuilderImages = ref<any[]>([]);

const isFetchingChartsAndImages = ref<boolean>(true);

const builderImage = ref(props.source?.builderImage || '');

// Reactive State
const gitSkipTypeReset = ref(false);
const archive = reactive({
  tarball: props.source?.archive?.tarball || '',
  fileName: props.source?.type === 'folder' ? props.application?.origin?.path : props.source?.archive?.fileName || '',
});

const container = reactive({
  url: props.source?.container?.url || ''
});

const gitUrl = reactive({
  url: props.source?.gitUrl?.url || '',
  branch: props.source?.gitUrl?.branch || '',
  validGitUrl: props.source?.gitUrl?.url ? true : false,
  gitconfig: props.source?.gitUrl?.gitconfig || ''
});

const git = reactive({
  usernameOrOrg: props.source?.git?.usernameOrOrg || '',
  repo: props.source?.git?.repo || '',
  commit: props.source?.git?.commit || '',
  branch: props.source?.git?.branch || '',
  url: props.source?.git?.url || '',
  sourceData: props.source?.git?.sourceData || {
    repos: [],
    branches: [],
    commits: []
  },
  gitconfig: props.source?.git?.gitconfig || ''
});

const appChart = ref(props.application.configuration?.appchart || props.source?.appChart || '');
const type = ref(props.source?.type || APPLICATION_SOURCE_TYPE.FOLDER);

// Derived and Computed
const types = Object.values(APPLICATION_SOURCE_TYPE).map(value => ({
  label: t(`epinio.applications.steps.source.${ value }.label`),
  value
}));

const namespaces = computed(() => sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'name', false));

// Get the builder images from the store, add custom option and format for dropdown
const allBuilderImages = computed(() => {
  const catalogImages = sortBy(builderImages.value, 'meta.name', false).map((bi: any) => ({
    value: bi.image,
    label: `${bi.meta.name} (${bi.short_description})`,
    default: bi.default
  }))
  const customOption = {
    value: 'custom',
    label: 'Custom',
    default: false
  }
  return [...catalogImages, customOption];
});

const selectedBuilderImage = computed(() => {
  return allBuilderImages.value.some(
    (bi) => bi.value === builderImage.value
  )
    ? builderImage.value
    : 'custom';
});

const isCustomBuilderImage = computed(
  () => selectedBuilderImage.value === 'custom'
);

const showBuilderImage = computed(() =>
  [APPLICATION_SOURCE_TYPE.ARCHIVE, APPLICATION_SOURCE_TYPE.FOLDER, APPLICATION_SOURCE_TYPE.GIT_URL, APPLICATION_SOURCE_TYPE.GIT_HUB, APPLICATION_SOURCE_TYPE.GIT_LAB].includes(type.value)
);

const gitSource = computed(() => ({
  type: type.value,
  selectedAccOrOrg: git.usernameOrOrg,
  selectedRepo: git.repo,
  selectedBranch: git.branch,
  selectedCommit: { sha: git.commit },
  gitconfig: git.gitconfig
}));

const valid = ref(validate());

const fetchGitConfigs = async () => {
  if (cachedGitConfigs.value.length > 0) {
    gitConfigs.value = cachedGitConfigs.value;
    return;
  }
  isLoadingGitConfigs.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: '/api/v1/gitconfigs',
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.GIT_CONFIG, ...item })
    ));
    gitConfigs.value = classifiedData;
    cachedGitConfigs.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch git configs', error);
  } finally {
    isLoadingGitConfigs.value = false;
  }
};

async function searchGitConfigs(query: string) {
  isLoadingGitConfigs.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/gitconfigs?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.GIT_CONFIG, ...item })
    ));
    gitConfigs.value = classifiedData;
  } catch {
    gitConfigs.value = [];
  } finally {
    isLoadingGitConfigs.value = false;
  }
}

const fetchAppCharts = async () => {
  if (cachedAppCharts.value.length > 0) {
    appCharts.value = cachedAppCharts.value;
    return;
  }
  isLoadingAppCharts.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: '/api/v1/appcharts',
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP_CHART, ...item })
    ));
    appCharts.value = classifiedData;
    cachedAppCharts.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch app charts', error);
  } finally {
    isLoadingAppCharts.value = false;
  }
};

async function searchAppCharts(query: string) {
  isLoadingAppCharts.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/appcharts?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP_CHARTS, ...item })
    ));
    appCharts.value = classifiedData;
  } catch {
    appCharts.value = [];
  } finally {
    isLoadingAppCharts.value = false;
  }
}

const fetchBuilderImages = async () => {
  if (cachedBuilderImages.value.length > 0) {
    builderImages.value = cachedBuilderImages.value;
    return;
  }
  isLoadingBuilderImages.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: '/api/v1/builderimages',
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.BUILDER_IMAGE, ...item })
    ));
    builderImages.value = classifiedData;
    cachedBuilderImages.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch builder images', error);
  } finally {
    isLoadingBuilderImages.value = false;
  }
};

async function searchBuilderImages(query: string) {
  isLoadingBuilderImages.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/builderimages?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.BUILDER_IMAGE, ...item })
    ));
    builderImages.value = classifiedData;
  } catch {
    builderImages.value = [];
  } finally {
    isLoadingBuilderImages.value = false;
  }
}

watch(type, () => {
  if (gitSkipTypeReset.value) {
    gitSkipTypeReset.value = false;
  } else {
    git.usernameOrOrg = '';
    git.repo = '';
    git.commit = '';
    git.branch = '';
    git.url = '';
    git.sourceData = {
      repos: [],
      branches: [],
      commits: []
    };
    git.gitconfig = '';
  }
  update();
});

watch(valid, (val) => {
  emit('valid', val);
});

function validate() {
  switch (type.value) {
    case APPLICATION_SOURCE_TYPE.ARCHIVE:
    case APPLICATION_SOURCE_TYPE.FOLDER:
      return !!archive.tarball && !!builderImage.value;
    case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
      return !!container.url;
    case APPLICATION_SOURCE_TYPE.GIT_URL:
      return !!gitUrl.url && !!gitUrl.branch && !!builderImage.value && !!gitUrl.validGitUrl;
    case APPLICATION_SOURCE_TYPE.GIT_HUB:
    case APPLICATION_SOURCE_TYPE.GIT_LAB:
      return !!git.usernameOrOrg && !!git.url && !!git.repo && !!git.branch && !!git.commit && !!builderImage.value;
  }
}

function update() {
  emit('change', {
    type: type.value,
    archive,
    container,
    gitUrl,
    builderImage: builderImage.value,
    appChart: appChart.value,
    git
  });
  valid.value = validate();
}

function updateAppInfo(info: EpinioAppInfo) {
  emit('changeAppInfo', info);
}

function updateConfigurations(configs: string[]) {
  emit('changeAppConfig', configs);
}

function handleBuilderImageDropdownChange(value: string) {
  if (value === 'custom') {
    builderImage.value = '';
  } else {
    builderImage.value = value;
  }
  update();
}

function gitUpdate({ repo, selectedAccOrOrg, branch, commit, sourceData, gitconfig }: any) {
  // GitHub always has an account/org selected; GitLab with a gitconfig lists
  // membership projects with no account/org, so only require it outside that case.
  const hasOwner = type.value === 'gitlab' || !!selectedAccOrOrg;
  if (hasOwner && !!repo && !!commit && !!branch) {
    git.usernameOrOrg = selectedAccOrOrg;
    // GitLab projects carry their own canonical URL (web_url), which is correct
    // for gitlab.com, enterprise instances, and the membership flow where no
    // account/org is picked. GitHub still builds from the selected org + repo.
    // Both providers return the repo's canonical URL on its own instance
    // (GitLab: web_url, GitHub: html_url), which is correct for SaaS and
    // enterprise alike. Fall back to building from the hardcoded base only if
    // that field is missing.
    git.url = type.value === 'gitlab'
      ? (repo.web_url || `${GIT_BASE_URL[type.value]}/${repo.path_with_namespace}`)
      : (repo.html_url || `${GIT_BASE_URL[type.value]}/${selectedAccOrOrg}/${repo.name}`);
    git.commit = commit;
    git.branch = branch;
    git.repo = repo;
    git.sourceData = sourceData;
    git.gitconfig = gitconfig;
    update();
    emit('valid', true);
  } else {
    update();
    emit('valid', false);
  }
}

function urlRule() {
  if (!gitUrl.url) return;

  const gitRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/gm;
  const result = gitRegex.exec(gitUrl.url);

  if (result && gitUrl.url === result[0]) {
    gitUrl.validGitUrl = true;
  } else {
    gitUrl.validGitUrl = false;
  }
}

function onFileSelected(file: File) {
  archive.tarball = file;
  archive.fileName = file.name;
  update();
}

function handleArchiveFileClick() {
  if (archiveFileInput.value) {
    fileDialogActive.value = true;
    archiveFileInput.value.click();
  }
}

function handleArchiveFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    onFileSelected(input.files[0]);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function handleFromManifestClick() {
  if (manifestFileInput.value) {
    fileDialogActive.value = true;
    manifestFileInput.value.click();
  }
}

function handleManifestFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    onManifestFileSelected(input.files[0] as any);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function onManifestFileSelected(file: string) {
  try {
    const parsed: any = jsyaml.load(file);
    const manifestType = AppUtils.getManifestSourceType(parsed.origin);
    gitSkipTypeReset.value = true;
    type.value = manifestType;

    switch (manifestType) {
      case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
        container.url = parsed.origin.container;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_URL:
        gitUrl.url = parsed.origin.git.url;
        gitUrl.branch = parsed.origin.git.revision;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_HUB:
      case APPLICATION_SOURCE_TYPE.GIT_LAB:
        Object.assign(git, AppUtils.getGitData(parsed.origin.git));
        break;
    }

    if (parsed.configuration) {
      appChart.value = parsed.configuration.appchart;
    }

    const appInfo: EpinioAppInfo = {
      meta: {
        name: parsed.name || '',
        namespace: namespaces.value?.[0]?.name || ''
      },
      configuration: {
        configurations: parsed.configuration?.configurations || [],
        instances: parsed.configuration.instances ?? 1,
        environment: parsed.configuration.environment || {},
        settings: parsed.configuration?.settings || {},
        routes: parsed.configuration.routes || []
      }
    };

    store.$router.replace({ query: { from: EPINIO_APP_MANIFEST } });
    update();
    updateAppInfo(appInfo);
    updateConfigurations(parsed.configuration.configurations || []);
  } catch (e) {
    console.error('Failed to parse manifest:', e);
  }
}

function handleFolderFileClick() {
  if (folderFileInput.value) {
    fileDialogActive.value = true;
    folderFileInput.value.click();
  }
}

function handleFolderFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    onFolderSelected(Array.from(input.files) as FileWithRelativePath[]);
    input.value = ''; // Clear the input so the same folder can be selected again if needed
  }
}

function onFolderSelected(files: FileWithRelativePath | FileWithRelativePath[]) {
  const safeFiles = Array.isArray(files) ? files : [files];
  let folderName = '';

  for (const f of safeFiles) {
    const paths = f.webkitRelativePath.split('/');
    if (paths.length > 1) {
      if (!folderName) {
        folderName = paths[0];
      } else if (folderName !== paths[0]) {
        folderName = '';
        break;
      }
    }
  }

  const filesToZip = safeFiles.reduce((res, f) => {
    let path = f.webkitRelativePath;
    if (folderName) {
      const parts = path.split('/');
      parts.shift();
      path = parts.join('/');
    }
    res[path] = f;
    return res;
  }, {} as { [key: string]: any });

  generateZip(filesToZip).then((zip: any) => {
    archive.tarball = zip;
    archive.fileName = folderName || 'folder';
    update();
  });
}

onMounted(async () => {
  await fetchAppCharts();
  await fetchBuilderImages();
  // If no app chart is set from the source or application configuration, default to the standard app chart
  if (!appChart.value) {
    const standardAppChart = appCharts.value.find((ac) => ac.meta.name === 'standard');
    appChart.value = props.application.configuration?.appchart || props.source?.appChart || standardAppChart?.meta.name || appCharts.value[0]?.meta.name || '';
  }
  // If no builder image is set from the source, default to the info default or the first in the catalog
  if (!builderImage.value) {
    const defaultImage = allBuilderImages.value.find((bi: any) => bi.default);
    builderImage.value = defaultImage ? defaultImage.value : allBuilderImages.value[0]?.value || '';
  }
  isFetchingChartsAndImages.value = false;
  update();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-dropdown", {
        style: {"flex":"1"},
        options: _unref(types),
        value: type.value,
        label: "Source Type",
        required: true,
        placeholder: "Select a source type",
        "data-testid": "epinio_app-source_type",
        onDropdownChange: _cache[0] || (_cache[0] = (e) => type.value = e.detail.value)
      }, null, 40, _hoisted_3),
      _cache[9] || (_cache[9] = _createTextVNode()),
      _createElementVNode("trailhand-button", {
        variant: "alternate",
        "data-testid": "epinio_app-source_manifest",
        onButtonClick: handleFromManifestClick
      }, "\n        From Manifest\n      ", 32),
      _cache[10] || (_cache[10] = _createTextVNode()),
      _createElementVNode("input", {
        ref_key: "manifestFileInput",
        ref: manifestFileInput,
        type: "file",
        class: "hidden-file-input",
        onChange: handleManifestFileChange,
        onCancel: _cache[1] || (_cache[1] = ($event: any) => (fileDialogActive.value = false))
      }, null, 544)
    ]),
    _cache[32] || (_cache[32] = _createTextVNode()),
    (type.value === _unref(APPLICATION_SOURCE_TYPE).ARCHIVE)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.archive.file.label')), 1),
          _cache[13] || (_cache[13] = _createTextVNode()),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("trailhand-text-input", {
              style: {"flex":"1"},
              value: archive.fileName,
              "data-testid": "epinio_app-source_archive_name",
              disabled: true,
              label: _unref(t)('epinio.applications.steps.source.archive.file.inputLabel'),
              required: true
            }, null, 8, _hoisted_6),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "alternate",
              "data-testid": "epinio_app-source_archive_file",
              onButtonClick: handleArchiveFileClick
            }, "\n            Select File\n          ", 32),
            _cache[12] || (_cache[12] = _createTextVNode()),
            _createElementVNode("input", {
              ref_key: "archiveFileInput",
              ref: archiveFileInput,
              type: "file",
              class: "hidden-file-input",
              accept: ".zip, .tar, .gz, .bz2, .xz",
              onChange: handleArchiveFileChange,
              onCancel: _cache[2] || (_cache[2] = ($event: any) => (fileDialogActive.value = false))
            }, null, 544)
          ])
        ]))
      : (type.value === _unref(APPLICATION_SOURCE_TYPE).FOLDER)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.folder.file.label')), 1),
            _cache[16] || (_cache[16] = _createTextVNode()),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("trailhand-text-input", {
                style: {"flex":"1"},
                value: archive.fileName,
                "data-testid": "epinio_app-source_folder_name",
                disabled: true,
                label: _unref(t)('epinio.applications.steps.source.folder.file.inputLabel'),
                required: true
              }, null, 8, _hoisted_9),
              _cache[14] || (_cache[14] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "alternate",
                "data-testid": "epinio_app-source_folder_file",
                onButtonClick: handleFolderFileClick
              }, "\n            Select Folder\n          ", 32),
              _cache[15] || (_cache[15] = _createTextVNode()),
              _createElementVNode("input", {
                ref_key: "folderFileInput",
                ref: folderFileInput,
                type: "file",
                webkitdirectory: "",
                class: "hidden-file-input",
                onChange: handleFolderFileChange,
                onCancel: _cache[3] || (_cache[3] = ($event: any) => (fileDialogActive.value = false))
              }, null, 544)
            ])
          ]))
        : (type.value === _unref(APPLICATION_SOURCE_TYPE).CONTAINER_URL)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.container_url.url.label')), 1),
              _cache[17] || (_cache[17] = _createTextVNode()),
              _createElementVNode("trailhand-text-input", {
                style: {"width":"100%"},
                value: container.url,
                "data-testid": "epinio_app-source_container",
                label: _unref(t)('epinio.applications.steps.source.container_url.url.inputLabel'),
                required: true,
                onTextInputChange: _cache[4] || (_cache[4] = (e) => { container.url = e.detail.value; update(); })
              }, null, 40, _hoisted_11)
            ]))
          : (type.value === _unref(APPLICATION_SOURCE_TYPE).GIT_URL)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createElementVNode("div", _hoisted_12, [
                  _cache[18] || (_cache[18] = _createElementVNode("h3", null, "Git Config", -1)),
                  _cache[19] || (_cache[19] = _createTextVNode()),
                  _createVNode(ResourceDropdown, {
                    value: gitUrl.gitconfig,
                    options: gitConfigs.value.map((c) => ({ value: c.meta.name, label: c.meta.name })),
                    label: "Git Config",
                    disabled: isEdit.value,
                    onDropdownChange: (e) => { gitUrl.gitconfig = e.detail.value; update(); },
                    fetchAllResources: fetchGitConfigs,
                    searchResources: searchGitConfigs,
                    isLoading: isLoadingGitConfigs.value
                  }, null, 8, ["value", "options", "disabled", "onDropdownChange", "isLoading"])
                ]),
                _cache[23] || (_cache[23] = _createTextVNode()),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.url.label')), 1),
                  _cache[20] || (_cache[20] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: gitUrl.url,
                    "data-testid": "epinio_app-source_git-url",
                    label: _unref(t)('epinio.applications.steps.source.git_url.url.inputLabel'),
                    placeholder: 'https://github.com/{user or org}/{repository}',
                    required: true,
                    onTextInputChange: _cache[5] || (_cache[5] = (e) => { gitUrl.url = e.detail.value; urlRule(); update(); })
                  }, null, 40, _hoisted_14),
                  _cache[21] || (_cache[21] = _createTextVNode()),
                  (gitUrl.url && !gitUrl.validGitUrl)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.error.label')), 1))
                    : _createCommentVNode("", true)
                ]),
                _cache[24] || (_cache[24] = _createTextVNode()),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.branch.label')), 1),
                  _cache[22] || (_cache[22] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: gitUrl.branch,
                    "data-testid": "epinio_app-source_git-branch",
                    label: _unref(t)('epinio.applications.steps.source.git_url.branch.inputLabel'),
                    required: true,
                    disabled: !gitUrl.validGitUrl,
                    onTextInputChange: _cache[6] || (_cache[6] = (e) => { gitUrl.branch = e.detail.value; update(); })
                  }, null, 40, _hoisted_17)
                ])
              ], 64))
            : (_openBlock(), _createBlock(GitPicker, {
                key: 4,
                value: gitSource.value,
                "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((gitSource).value = $event)),
                type: type.value,
                gitConfigs: gitConfigs.value,
                fetchGitConfigs: fetchGitConfigs,
                searchGitConfigs: searchGitConfigs,
                isLoadingGitConfigs: isLoadingGitConfigs.value,
                onChange: gitUpdate
              }, null, 8, ["value", "type", "gitConfigs", "isLoadingGitConfigs"])),
    _cache[33] || (_cache[33] = _createTextVNode()),
    _createElementVNode("div", _hoisted_18, [
      _cache[30] || (_cache[30] = _createElementVNode("h3", null, "Advanced Settings", -1)),
      _cache[31] || (_cache[31] = _createTextVNode()),
      (isFetchingChartsAndImages.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_19, _cache[25] || (_cache[25] = [
            _createElementVNode("trailhand-loading-spinner", null, null, -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createVNode(ResourceDropdown, {
              value: appChart.value,
              options: appCharts.value.map((ap) => ({
            value: ap.meta.name,
            label: `${ap.meta.name} (${ap.short_description})`
          })),
              label: _unref(t)('epinio.applications.steps.source.archive.appchart.label'),
              disabled: isEdit.value || isView.value,
              placeholder: "Select an application chart",
              onDropdownChange: (e) => { appChart.value = e.detail.value; update(); },
              fetchAllResources: fetchAppCharts,
              searchResources: searchAppCharts,
              isLoading: isLoadingAppCharts.value
            }, null, 8, ["value", "options", "label", "disabled", "onDropdownChange", "isLoading"]),
            _cache[29] || (_cache[29] = _createTextVNode()),
            (showBuilderImage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  _cache[26] || (_cache[26] = _createElementVNode("h4", null, "Paketo Builder Image", -1)),
                  _cache[27] || (_cache[27] = _createTextVNode()),
                  _createVNode(ResourceDropdown, {
                    value: selectedBuilderImage.value,
                    options: allBuilderImages.value,
                    label: "Builder Image",
                    onDropdownChange: (e) => { handleBuilderImageDropdownChange(e.detail.value) },
                    fetchAllResources: fetchBuilderImages,
                    searchResources: searchBuilderImages,
                    isLoading: isLoadingBuilderImages.value
                  }, null, 8, ["value", "options", "onDropdownChange", "isLoading"]),
                  _cache[28] || (_cache[28] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: builderImage.value,
                    "data-testid": "epinio_app-source_builder-value",
                    disabled: !isCustomBuilderImage.value,
                    onTextInputChange: _cache[8] || (_cache[8] = (e) => { builderImage.value = e.detail.value; update(); })
                  }, null, 40, _hoisted_22)
                ]))
              : _createCommentVNode("", true)
          ]))
    ])
  ]))
}
}

})